import { Box, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { memo, useMemo } from 'react';
import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import { LuFileText } from 'react-icons/lu';

import {
  ADVISOR_ASSISTANT_BUBBLE_BG,
  ADVISOR_PANEL_NEUTRAL_100,
  ADVISOR_PANEL_NEUTRAL_400,
  ADVISOR_PANEL_PRIMARY_TEXT,
  ADVISOR_PANEL_PRIMARY_TEXT_HOVER,
} from './advisor-panel-tokens';

const ADVISOR_SOURCE_ROW_ICON_COLOR = 'neutral.350';
/**
 * Figma Neutral/500 — primary text + source link color ([13101-69088](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13101-69088)).
 */
const ADVISOR_FIGMA_NEUTRAL_500 = '#000000';

const ALLOWED_MARKDOWN_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

const ADVISOR_ASSISTANT_MARKDOWN_COMPONENTS: Components = {
  // Do not spread react-markdown's full anchor props onto `<a>`: partner-app typecheck
  // resolves this package with a different `@types/react` copy, and `ref` then fails to unify.
  a: ({ node: _node, children, href, title, className, id }) => {
    const safeHref = sanitizeMarkdownHref(href);
    if (!safeHref) {
      return (
        <span className={className} id={id}>
          {children}
        </span>
      );
    }
    return (
      <a
        href={safeHref}
        title={title}
        className={className}
        id={id}
        rel="noopener noreferrer"
        target="_blank"
      >
        {children}
      </a>
    );
  },
};

/**
 * Figma Kin Frame 31405 ([13101-69088](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13101-69088)):
 * Default/Regular 14/20, Neutral/400 body; opening line + full-line bold Medium/black; list `ms-[21px]`; `gap-[8px]` block rhythm.
 */
const ADVISOR_ASSISTANT_MARKDOWN_CSS = {
  '& p': {
    margin: 0,
    fontSize: '14px',
    lineHeight: '20px',
    color: ADVISOR_PANEL_NEUTRAL_400,
  },
  '& > p:first-of-type': { color: ADVISOR_FIGMA_NEUTRAL_500 },
  '& p:has(> strong:only-child)': { color: ADVISOR_FIGMA_NEUTRAL_500 },
  '& p + p': { marginTop: '8px' },
  '& ul, & ol': {
    margin: '8px 0',
    paddingLeft: '21px',
    fontSize: '14px',
    lineHeight: '20px',
    color: ADVISOR_PANEL_NEUTRAL_400,
    listStylePosition: 'outside' as const,
  },
  '& ul': { listStyleType: 'disc' as const },
  '& ul ul': { listStyleType: 'circle' as const },
  '& ol': { listStyleType: 'decimal' as const },
  '& li': {
    marginBottom: '2px',
    display: 'list-item' as const,
    color: ADVISOR_PANEL_NEUTRAL_400,
  },
  '& strong': { fontWeight: 600, color: ADVISOR_FIGMA_NEUTRAL_500 },
  // Inline links are not in the Kin Figma frames (only the Helpful-articles source rows are).
  // They used to inherit Neutral/500 with no underline and were indistinguishable from bold
  // text, so users missed clickable citations and deep links (tax-research government
  // references, filtered Transactions views). Kin's action color plus a persistent underline
  // is the one cue that also survives color-blindness.
  '& a': {
    color: ADVISOR_PANEL_PRIMARY_TEXT,
    textDecoration: 'underline',
    textDecorationThickness: '1px',
    textUnderlineOffset: '2px',
  },
  '& a:hover': { color: ADVISOR_PANEL_PRIMARY_TEXT_HOVER },
  '& a:focus-visible': {
    outline: `2px solid ${ADVISOR_PANEL_PRIMARY_TEXT}`,
    outlineOffset: '2px',
    borderRadius: '2px',
  },
  '& code': {
    fontSize: '13px',
    padding: '1px 4px',
    borderRadius: '4px',
    background: '#f5f5f5',
  },
};

export interface AdvisorAssistantMarkdownProps {
  text: string;
}

type SourceListItem = { title: string; href?: string };

/** Legacy Intercom-style paths (``/en/articles/{id}-{slug}``) 404 on DevRev; slug-only DevRev URLs work. */
const LEGACY_HELP_CENTER_ARTICLE_PATH = /^\/en\/articles?\/\d+-([^/?#]+)$/i;

function rewriteLegacyHelpCenterArticlePath(url: URL): URL {
  const legacy = LEGACY_HELP_CENTER_ARTICLE_PATH.exec(url.pathname);
  if (!legacy?.[1]) {
    return url;
  }
  const slug = legacy[1];
  const rewritten = new URL(url.href);
  rewritten.pathname = `/en-US/trykintsugi/article/${slug}`;
  return rewritten;
}

/** Only Help Center hosts become clickable; everything else stays title-only (defense-in-depth vs raw ``href``). */
function normalizeHelpfulArticleHref(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return undefined;
  }
  if (url.hostname.toLowerCase() !== 'help.trykintsugi.com') {
    return undefined;
  }
  if (url.protocol === 'http:') {
    url.protocol = 'https:';
  }
  if (url.protocol !== 'https:') {
    return undefined;
  }
  return rewriteLegacyHelpCenterArticlePath(url).href;
}

/** Allowlist http/https/mailto for inline markdown links; Help Center URLs get legacy-path rewriting. */
function sanitizeMarkdownHref(href: string | undefined): string | undefined {
  if (href === undefined) return undefined;
  const trimmed = href.trim();
  if (!trimmed) return undefined;

  const helpCenterHref = normalizeHelpfulArticleHref(trimmed);
  if (helpCenterHref !== undefined) {
    return helpCenterHref;
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return undefined;
  }

  if (!ALLOWED_MARKDOWN_LINK_PROTOCOLS.has(parsed.protocol.toLowerCase())) {
    return undefined;
  }

  return parsed.href;
}

/**
 * Detects the model’s “Helpful articles” heading; keeps a second regex for the common LLM typo “aritcles” — do not drop it or downstream list parsing will miss those headings.
 */
function isHelpfulArticlesHeading(line: string): boolean {
  const t = line.replace(/\*\*/g, '').trim();
  if (/^helpful\s+aritcles\s*:?\s*$/i.test(t)) return true;
  return /^helpful\s+articles\s*:?\s*$/i.test(t);
}

/**
 * Entire trimmed bullet is `[title](url)` (no trailing prose). Uses the last `)` as the link
 * closer so URLs may contain `)` (e.g. `javascript:alert(1)`).
 */
function parseWholeMarkdownLinkLine(trimmed: string): { title: string; href: string } | null {
  if (!trimmed.startsWith('[')) {
    return null;
  }
  const mid = trimmed.indexOf('](');
  if (mid < 1) {
    return null;
  }
  if (!trimmed.endsWith(')')) {
    return null;
  }
  const title = trimmed.slice(1, mid).trim();
  const href = trimmed.slice(mid + 2, -1).trim();
  if (!title || !href) {
    return null;
  }
  return { title, href };
}

/**
 * Parse one “Helpful articles” bullet for the structured source list.
 * Returns `null` when the bullet embeds a markdown link but is not **only** a link (or bold-wrapped link),
 * so callers can bail out to full markdown and avoid dropping trailing prose.
 */
function tryParseHelpfulArticlesBullet(body: string): SourceListItem | null {
  const trimmed = body.trim();
  if (!trimmed) {
    return null;
  }
  const whole = parseWholeMarkdownLinkLine(trimmed);
  if (whole) {
    return {
      title: whole.title,
      href: normalizeHelpfulArticleHref(whole.href),
    };
  }
  if (trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.length > 4) {
    const inner = trimmed.slice(2, -2).trim();
    const innerLink = parseWholeMarkdownLinkLine(inner);
    if (innerLink) {
      return {
        title: innerLink.title,
        href: normalizeHelpfulArticleHref(innerLink.href),
      };
    }
  }
  if (/\[[^\]]+\]\([^)]+\)/.test(trimmed)) {
    return null;
  }
  let title = trimmed;
  if (title.startsWith('**') && title.endsWith('**') && title.length > 4) {
    title = title.slice(2, -2).trim();
  }
  return { title };
}

/**
 * Split markdown when the model emits a “Helpful articles” **heading line** (parse trigger) plus bullet list.
 * That heading is **not** shown in the UI: Figma is body → divider → **Frame 31398** rows only (file + link),
 * see [13101-69088](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13101-69088).
 * Only the **first** matching heading + list is split; later sections stay in markdown.
 */
function trySplitHelpfulArticlesMarkdown(text: string): {
  prefix: string;
  items: SourceListItem[];
  suffix: string;
} | null {
  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    if (!isHelpfulArticlesHeading(lines[i])) continue;
    const items: SourceListItem[] = [];
    let j = i + 1;
    while (j < lines.length && lines[j].trim() === '') {
      j++;
    }
    while (j < lines.length) {
      const raw = lines[j];
      if (raw.trim() === '') {
        break;
      }
      const bullet = /^[ \t]*[-*][ \t]+(.+)$/.exec(raw);
      if (!bullet) {
        break;
      }
      const item = tryParseHelpfulArticlesBullet(bullet[1]);
      if (item === null) {
        return null;
      }
      items.push(item);
      j++;
    }
    if (items.length === 0) {
      continue;
    }
    const prefix = lines.slice(0, i).join('\n');
    const suffix = lines.slice(j).join('\n');
    return { prefix, items, suffix };
  }
  return null;
}

/**
 * When source rows render below, remove Help Center URLs repeated as ``(https://help…)`` or ``(help.trykintsugi.com)``
 * from prose so citations match Figma (divider + file rows only, no duplicate parentheticals).
 */
function stripHelpCenterParentheticalCitations(markdown: string): string {
  let s = markdown.replace(/\s*\(\s*https?:\/\/help\.trykintsugi\.com[^)]*\)/gi, '');
  s = s.replace(/\s*\(\s*help\.trykintsugi\.com\s*\)/gi, '');
  return s.replace(/\n{3,}/g, '\n\n');
}

/**
 * Figma Kin — **Frame 31398** ([13101-69106](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13101-69106)):
 * `flex` row **`gap-[4px]`**, **18px** file icon, link **Small/Regular** **12px / 18px**, **Neutral/500** text `#000000`.
 * (File vector uses **Neutral/350** via `neutral.350` — see `ADVISOR_SOURCE_ROW_ICON_COLOR`.)
 * The file shows **one** row; stacked rows use the same **8px** vertical rhythm as **Frame 31395** `gap-[8px]` ([13101-69088](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13101-69088)).
 */
interface AssistantHelpfulSourcesSectionProps {
  items: SourceListItem[];
  showTopDivider: boolean;
}

function AssistantHelpfulSourcesSection({ items, showTopDivider }: AssistantHelpfulSourcesSectionProps) {
  // Figma 13101:69088 — 8px above/below divider (`gap-[8px]`); divider #E7E7E7.
  return (
    <VStack
      align="stretch"
      gap={0}
      mt={showTopDivider ? '8px' : 0}
      pt={showTopDivider ? '8px' : 0}
      borderTopWidth={showTopDivider ? '1px' : 0}
      borderTopColor={ADVISOR_PANEL_NEUTRAL_100}
    >
      <VStack
        as="ul"
        aria-label="Help center article links"
        align="stretch"
        gap="8px"
        listStyleType="none"
        margin={0}
        padding={0}
        paddingInlineStart={0}
      >
        {items.map((it, idx) => (
          <HStack
            as="li"
            key={`${idx}-${it.title}`}
            columnGap="4px"
            align="center"
            minH="18px"
            minW={0}
            listStyleType="none"
          >
            <Icon
              as={LuFileText}
              boxSize="18px"
              color={ADVISOR_SOURCE_ROW_ICON_COLOR}
              flexShrink={0}
              aria-hidden
            />
            {it.href ? (
              <Text
                asChild
                flex={1}
                minW={0}
                fontSize="12px"
                lineHeight="18px"
                fontWeight="normal"
                color={ADVISOR_FIGMA_NEUTRAL_500}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                _hover={{ textDecoration: 'underline' }}
              >
                <a href={it.href} rel="noopener noreferrer" target="_blank">
                  {it.title}
                </a>
              </Text>
            ) : (
              <Text
                flex={1}
                minW={0}
                fontSize="12px"
                lineHeight="18px"
                fontWeight="normal"
                color={ADVISOR_FIGMA_NEUTRAL_500}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {it.title}
              </Text>
            )}
          </HStack>
        ))}
      </VStack>
    </VStack>
  );
}

/**
 * Renders assistant markdown in the standard advisor chat bubble (typography, lists, code).
 * Place beside the app AI icon in an `HStack`; layout chrome stays in each shell.
 * Figma Kin — AI Answer plate ([13101-69655](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13101-69655)):
 * fill **`#F4F5F7`**, **16px** inset for body + source row (`ml-[16px] mt-[16px]` on inner frames in [13101-69088](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13101-69088)),
 * **top-left square** (0 radius), **8px** other corners, **no** shadow. Dividers **`#E7E7E7`** 1px — {@link ADVISOR_PANEL_NEUTRAL_100}.
 */
function AdvisorAssistantMarkdownInner({ text }: AdvisorAssistantMarkdownProps) {
  const splitDisplay = useMemo(() => {
    const parsed = trySplitHelpfulArticlesMarkdown(text);
    if (!parsed) {
      return null;
    }
    const prefixMd = stripHelpCenterParentheticalCitations(parsed.prefix);
    const suffixMd = stripHelpCenterParentheticalCitations(parsed.suffix);
    return {
      prefixMd,
      suffixMd,
      hasPrefix: prefixMd.trim().length > 0,
      hasSuffix: suffixMd.trim().length > 0,
      items: parsed.items,
    };
  }, [text]);

  return (
    <Box
      data-testid="advisor-assistant-markdown"
      p="16px"
      borderTopLeftRadius={0}
      borderTopRightRadius="8px"
      borderBottomRightRadius="8px"
      borderBottomLeftRadius="8px"
      bg={ADVISOR_ASSISTANT_BUBBLE_BG}
    >
      {splitDisplay ? (
        <>
          {splitDisplay.hasPrefix ? (
            <Box css={ADVISOR_ASSISTANT_MARKDOWN_CSS}>
              <Markdown components={ADVISOR_ASSISTANT_MARKDOWN_COMPONENTS}>{splitDisplay.prefixMd}</Markdown>
            </Box>
          ) : null}
          <AssistantHelpfulSourcesSection
            items={splitDisplay.items}
            showTopDivider={splitDisplay.hasPrefix}
          />
          {splitDisplay.hasSuffix ? (
            <Box mt="8px" css={ADVISOR_ASSISTANT_MARKDOWN_CSS}>
              <Markdown components={ADVISOR_ASSISTANT_MARKDOWN_COMPONENTS}>{splitDisplay.suffixMd}</Markdown>
            </Box>
          ) : null}
        </>
      ) : (
        <Box css={ADVISOR_ASSISTANT_MARKDOWN_CSS}>
          <Markdown components={ADVISOR_ASSISTANT_MARKDOWN_COMPONENTS}>{text}</Markdown>
        </Box>
      )}
    </Box>
  );
}

/**
 * Memoized so parent transcript re-renders do not re-parse markdown for unchanged ``text`` props (same string
 * reference after sibling row memoization, or when the bubble is unchanged).
 */
export const AdvisorAssistantMarkdown = memo(AdvisorAssistantMarkdownInner);
AdvisorAssistantMarkdown.displayName = 'AdvisorAssistantMarkdown';
